/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useMemo, useState } from 'react';

import { jsx } from '@compiled/react';

import { fg } from '@atlaskit/platform-feature-flags';

import { useMenuControl } from '../../../context';
import { useUniversalCreateAnalytics } from '../../../hooks';
import { withIntlProvider } from '../../../services';
import type { MenuItem, NamedItems } from '../../../types';
import MenuContentItem from '../MenuItem';
import MenuSectionPrimitive, { MIN_ITEMS } from '../MenuSectionPrimitive';

const MAX_ITEMS = 10;

export type NativeMenuSectionProps = {
	namedItems: NamedItems;
	sectionIndex: number;
	sectionStartIndex: number;
	totalMenuItemsCount: number;
};

function NativeMenuSection({
	namedItems,
	sectionIndex,
	totalMenuItemsCount,
	sectionStartIndex,
}: NativeMenuSectionProps) {
	const [showAll, setShowAll] = useState(false);
	const { fireNativeMenuItemClicked, fireNewNativeMenuItemClicked } = useUniversalCreateAnalytics();
	const { product } = useMenuControl();

	const sectionItemsCount = namedItems.items.length;

	const items = useMemo(() => {
		if (!sectionItemsCount) {
			return [];
		}

		if (!namedItems.foldable || sectionItemsCount <= MIN_ITEMS) {
			return namedItems.items;
		}

		return namedItems.items.slice(0, showAll ? MAX_ITEMS : MIN_ITEMS);
	}, [namedItems, showAll, sectionItemsCount]);

	const toggleShowMore = useCallback(() => {
		setShowAll((prev) => !prev);
	}, [setShowAll]);

	const menuItemWithAnalytics = useCallback(
		(item: MenuItem, itemIndex: number): MenuItem => {
			const fireAnalytics = (isRightClick: boolean) => {
				const itemAti = item.ati.toString();
				if (fg('navx-uc-unified-instrumentation')) {
					fireNewNativeMenuItemClicked({
						ati: itemAti,
						menuItemsCount: totalMenuItemsCount, // Total number of items in the native sections part of menu
						menuItemIndex: sectionStartIndex + itemIndex, // Position of the item in the native sections part of menu
						groupItemProduct: product,
						groupItemsCount: sectionItemsCount, // Number of items in the section
						groupItemsIndex: itemIndex, // Position of the item in the section
						isRightClick,
					});
				} else {
					fireNativeMenuItemClicked({ ati: itemAti });
				}
			};

			return {
				...item,
				onClick: (event: React.MouseEvent | React.KeyboardEvent) => {
					fireAnalytics(false);
					item.onClick?.(event);
				},
				onContextMenu: (event: React.MouseEvent) => {
					fireAnalytics(true);
					item.onContextMenu?.(event);
				},
			};
		},
		[
			fireNativeMenuItemClicked,
			fireNewNativeMenuItemClicked,
			totalMenuItemsCount,
			sectionStartIndex,
			sectionItemsCount,
			product,
		],
	);

	if (!namedItems?.items?.length) {
		return null;
	}

	return (
		<MenuSectionPrimitive
			sectionIndex={sectionIndex}
			itemLength={namedItems.items.length}
			foldable={namedItems.foldable}
			heading={namedItems.heading}
			showAll={showAll}
			toggleShowMore={toggleShowMore}
			isNative
		>
			{items.map((menuItem: MenuItem, index: number) => (
				<MenuContentItem
					item={menuItemWithAnalytics(menuItem, index)}
					key={`menu-section-${sectionIndex}-item-${index}`}
				/>
			))}
		</MenuSectionPrimitive>
	);
}

export default withIntlProvider(NativeMenuSection);
